import { Error } from '../error';
import Pagination from '@/components/Pagination';
import { useCohortEdit } from './hooks/useCohortEdit';
import { usePagination } from './hooks/usePagination';
import { useCohortSummary } from './hooks/useCohortSummary';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useReportingToolsStore } from '../../store/useReportingToolsStore';
import { SuspenseLoading } from '../loading';
import { CohortEditModalSkeleton } from './loading/edit-modal-skeleton';
import EdwinDialog from '@/components/Dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Button from '@/components/Button';
import { Controller } from 'react-hook-form';

export const CohortSummary = () => {
  const { data, error, isPending } = useCohortSummary();
  const { totalPages, currentPageData, currentPage, handlePageChange } = usePagination(data);
  const onOpenDialog = useReportingToolsStore((state) => state.onOpenDialog);
  const page = useReportingToolsStore((state) => state.page);
  const editModal = page?.cohortSummary?.editModal;
  const {
    isEditModalOpen,
    setIsEditModalOpen,
    loadingCohortDetails,
    loadingUpdateCohort,
    control,
    register,
    errors,
    fields,
    yearGroupOptions,
    handleOpenEditModal,
    handleAddClass,
    handleRemoveClass,
    handleSave,
  } = useCohortEdit();

  const KEYS_HEADER = [
    page?.cohortSummary?.table?.cohortName,
    page?.cohortSummary?.table?.schoolYear || 'School year',
    page?.cohortSummary?.table?.yearGroup,
    page?.cohortSummary?.table?.keyStage,
    page?.cohortSummary?.table?.classes,
    page?.cohortSummary?.table?.mostImpTrait,
    page?.cohortSummary?.table?.created,
    page?.cohortSummary?.table?.lastEdited,
    page?.cohortSummary?.table?.createdBy,
    page?.cohortSummary?.table?.lastUpdatedBy || 'Updated by',
    '',
  ];

  if (error) {
    return <Error error={error} />;
  }

  return (
    <div className="relative flex flex-col">
      {(isPending || loadingUpdateCohort) && <SuspenseLoading />}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
        <div className="flex items-center justify-start gap-4 p-5">
          <h2 className="text-sh-22-28 font-medium">{page?.cohortSummary?.title}</h2>
          <InformationCircleIcon
            className="h-6 w-6 cursor-pointer text-gray-400"
            onClick={() =>
              onOpenDialog({
                title: page?.cohortSummary?.dialogTitle || '',
                description: page?.cohortSummary?.dialogDescription || '',
              })
            }
          />
        </div>

        {currentPageData.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center rounded-2xl pb-5">
            <p className="text-sh-xs text-gray-500">{page?.cohortSummary?.noData}</p>
          </div>
        )}

        {currentPageData.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  {KEYS_HEADER.map((headerItem, index) => (
                    <th
                      key={index}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {headerItem}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 ">
                {currentPageData.map((cohortSummary, index) => {
                  if (cohortSummary == null) return null;
                  return (
                    <tr key={cohortSummary.cohortName || index} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {cohortSummary?.cohortName || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.schoolYear || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.yearGroup || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.keyStage || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.classes || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.mostImprovedTrait?.trait || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.createdAt
                          ? new Date(cohortSummary?.createdAt + 'T00:00:00-03:00').toLocaleDateString('en-GB')
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.updatedAt
                          ? new Date(cohortSummary?.updatedAt + 'T00:00:00-03:00').toLocaleDateString('en-GB')
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.createdBy || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {cohortSummary?.lastUpdatedBy || '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <button
                          type="button"
                          aria-label="Edit cohort"
                          className="inline-flex items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => handleOpenEditModal(cohortSummary)}>
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="border-t border-gray-200 bg-white px-4 py-3">
                <Pagination currentPage={currentPage} pageSize={totalPages} onChangePage={handlePageChange} />
              </div>
            )}
          </div>
        )}
      </div>
      <EdwinDialog
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        title={editModal?.editDialogTitle ?? 'Edit cohort'}
        body={
          <div className="space-y-4 text-left">
            {loadingCohortDetails ? (
              <CohortEditModalSkeleton loadingLabel={editModal?.editLoadingDetails} />
            ) : (
              <>
                <div>
                  <label htmlFor="edit-cohort-name" className="mb-2 block text-sm font-medium text-gray-700">
                    {editModal?.editCohortNameLabel ?? 'Cohort name'}
                  </label>
                  <Input id="edit-cohort-name" {...register('cohortName')} />
                  {errors.cohortName?.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.cohortName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="edit-cohort-year-group" className="mb-2 block text-sm font-medium text-gray-700">
                    {editModal?.editYearGroupLabel ?? 'Year group'}
                  </label>
                  <Controller
                    control={control}
                    name="yearGroupDocumentId"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger id="edit-cohort-year-group">
                          <SelectValue placeholder={editModal?.editYearGroupPlaceholder ?? 'Select year group'} />
                        </SelectTrigger>
                        <SelectContent>
                          {yearGroupOptions.map((yearGroup) => (
                            <SelectItem key={yearGroup.documentId} value={yearGroup.documentId}>
                              {yearGroup.yearGroup}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.yearGroupDocumentId?.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.yearGroupDocumentId.message}</p>
                  )}
                </div>

                <fieldset>
                  <legend className="mb-2 block text-sm font-medium text-gray-700">
                    {editModal?.editClassesLegend ?? 'Classes'}
                  </legend>
                  <div className="space-y-2">
                    {fields.map((field, index) => {
                      const classNameAriaLabel = (
                        editModal?.editClassNameAriaTemplate ?? 'Class name ${index}'
                      ).replace('${index}', String(index + 1));
                      return (
                        <div key={field.id} className="flex items-center gap-2">
                          <Input aria-label={classNameAriaLabel} {...register(`classNames.${index}.name`)} />
                          <button
                            type="button"
                            aria-label={editModal?.editRemoveClassAria ?? 'Remove class'}
                            className="inline-flex rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            onClick={() => handleRemoveClass(index)}>
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {errors.classNames?.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.classNames.message}</p>
                  )}
                  {fields.map((field, index) => (
                    <div key={`class-error-${field.id}`}>
                      {errors.classNames?.[index]?.name?.message && (
                        <p className="mt-1 text-sm text-red-600">{errors.classNames[index]?.name?.message}</p>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600"
                    onClick={handleAddClass}>
                    <PlusIcon className="h-4 w-4" />
                    {editModal?.editAddClassButton ?? 'Add class'}
                  </button>
                </fieldset>

                <div className="flex justify-end gap-3 pt-2">
                  <Button theme="outline" onClick={() => setIsEditModalOpen(false)}>
                    {editModal?.editCancelButton ?? 'Cancel'}
                  </Button>
                  <Button theme="solid" loading={loadingUpdateCohort} onClick={handleSave}>
                    {editModal?.editSaveButton ?? 'Save'}
                  </Button>
                </div>
              </>
            )}
          </div>
        }
      />
    </div>
  );
};
